import React, { useCallback, useEffect, useRef, useState } from 'react';
import './ParticipentInfo.css'
import UserAvatar from "../../../../../components/UserAvatar/UserAvatar.jsx";
import { useFloating, shift, flip, offset, autoUpdate } from "@floating-ui/react";
import HandRankBadge from "../HandRankBadge/HandRankBadge.jsx";
import ActionConfirmation from '../../../../../components/ActionConfirmation.js';
import { useTranslation } from 'react-i18next';
import { createPortal } from "react-dom";
import MediaContainer from '../MediaContainer/MediaContainer';
import { mapSocketFactory } from '../../../../../utils/socketFactory.js';
import { triggerHaloAnimation } from '../../../../../utils/haloUtils.js';
import { useHaloSocket } from '../../../../../hooks/useHaloSocket.js';
import { useSelector } from 'react-redux';
const ParticipantInfo = ({
    participant,
    participantAudioTrack,
    handRank,
    handleRestoreCamera,
    cameraDisabledLocal,
    handleCloseMicClick,
    handleConfirmCloseMic,
    confirmDialogMiCOpen,
    setconfirmDialogMicOpen,
    handleFireUserClick,
    handleConfirmFireUser,
    setConfirmFireUserDialogOpen,
    ConfirmFireUserDialogOpen,
    handleInviteToGame
}) => {
    const { t } = useTranslation();

    const [open, setOpen] = useState(false);
    const [metadata, setMetadata] = useState({});
    const [active, setActive] = useState(false);
    const isMobile = window.innerWidth < 480;
    const { refs, floatingStyles } = useFloating({
        placement: "bottom-end",
        middleware: [
            offset(6),
            flip(),
            shift(),
        ],
        whileElementsMounted: autoUpdate,
    });
    const participantAvatarRef = useRef(null);

    const handleHalo = useCallback((level) => {
        triggerHaloAnimation(participantAvatarRef.current, metadata?.id, level, 'flex');
    }, [metadata?.id]);

    // Use the hook - Handles connection, retry, and cleanup automatically
    useHaloSocket(metadata?.id, handleHalo);

    const { localAvatar } = useSelector((state) => state.auth);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                !refs.reference.current?.contains(e.target) &&
                !refs.floating.current?.contains(e.target)
            ) {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [refs.reference, refs.floating]);

    useEffect(() => {
        const handleMetadataChange = () => {
            try {
                console.log("Metadata changed for participant:", participant.participantInfo?.metadata);
                const meta = JSON.parse(participant.participantInfo?.metadata || "{}");
                setMetadata(meta);
            } catch {
                console.error("Failed to parse participant metadata");
            }
        };

        const setupMetadataListener = async () => {
            await participant.waitUntilActive();
            setActive(true);
            console.log("Participant is now active, loading metadata");
            handleMetadataChange();
            participant.on("ParticipantMetadataChanged", handleMetadataChange);
        };

        setupMetadataListener();

        return () => {
            participant.off("ParticipantMetadataChanged", handleMetadataChange);
        };
    }, [participant]);

    if (!participant || !active) {
        return null;
    }

    return (
        <>
            <MediaContainer
                mediaId={participant.participantInfo?.identity}
                canZoom={false} // ParticipantInfo cannot zoom
                canFullscreen={false} // ParticipantInfo cannot fullscreen
                canFloat={false}
                containerType="avatar"
            >
                <div
                    ref={participantAvatarRef}
                    style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                >
                    <div style={{ zIndex: 1, display: 'flex' }}>
                        <UserAvatar user={metadata} size={120} />
                    </div>
                </div>
                <HandRankBadge rank={handRank} />
                <span className={`mic-status ${participantAudioTrack ? 'mic-active' : 'mic-inactive'}`}>
                    {participantAudioTrack ?
                        <span className="material-symbols-outlined">
                            mic
                        </span>
                        :
                        <span className="material-symbols-outlined">
                            mic_off
                        </span>
                    }
                </span>

                <div className={`video-control-panel ${open ? 'visible' : ''}`}>
                    <div className='btn-video-control-panel menu-video-control-panel' ref={refs.setReference} onClick={() => setOpen((prev) => !prev)}>
                        <span className="material-symbols-outlined">
                            more_vert
                        </span>
                    </div>
                </div>

                <span className='participent-camera-status'>
                    <span className="material-symbols-outlined">
                        videocam_off
                    </span>
                </span>
                <span className="participant-name text-gray-800 font-medium">{metadata.name || "Anonyme"}</span>
            </MediaContainer>

            {open &&
                createPortal(
                    <div className='actions-menu-video' ref={refs.setFloating} style={
                        isMobile
                            ? { position: 'absolute', left: 0, right: 0, bottom: 0 }
                            : floatingStyles
                    }>
                        <ul>
                            {cameraDisabledLocal &&
                                <li className='action-menu-item' onClick={handleRestoreCamera}>
                                    <span className="material-symbols-outlined">
                                        videocam
                                    </span>
                                    afficher la caméra
                                </li>
                            }
                            {participantAudioTrack &&
                                <li className='action-menu-item' onClick={handleCloseMicClick}>
                                    <span className="material-symbols-outlined">
                                        mic_off
                                    </span>
                                    Couper le micro
                                </li>
                            }

                            <li className='action-menu-item'>
                                <span className="material-symbols-outlined">
                                    keep_public
                                </span>
                                Epingler
                            </li>

                            { localAvatar?.is_guest === 0 && 
                                <li className='action-menu-item' onClick={handleFireUserClick}>
                                    <span className="material-symbols-outlined">
                                        do_not_disturb_on
                                    </span>
                                    Exclusion
                                </li>
                            }
                            <li className='action-menu-item' onClick={() => {
                                setOpen(false);
                                handleInviteToGame('TICTACTOE');
                            }}>
                                <span className="material-symbols-outlined">
                                    sports_esports
                                </span>
                                Inviter au Tic-Tac-Toe
                            </li>
                        </ul>
                    </div>,
                    document.body
                )
            }

            <ActionConfirmation
                open={confirmDialogMiCOpen}
                title={t("close_mic_header")}
                msg={t("couper_Micor")}
                cancelText={t("cancel")}
                confirmText={t("confirm")}
                icon="mic_off"
                onClose={() => setconfirmDialogMicOpen(false)}
                onConfirm={handleConfirmCloseMic}
            />

            <ActionConfirmation
                open={ConfirmFireUserDialogOpen}
                onClose={() => setConfirmFireUserDialogOpen(false)}
                onConfirm={handleConfirmFireUser}
                title={t("virer_utilisateur_header")}
                msg={t("virer_utilisateur", { name: metadata.name })}
                variant="destructive"
                cancelText={t("cancel")}
                confirmText={t("confirm")}
            />

        </>
    );
};

export default ParticipantInfo;